

import { useState } from 'react';
import { Loader2, Upload, Download, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { authFetch } from '@/lib/auth-client';

export interface ManualPredictionCsvModalProps {
  open: boolean;
  mainEventId: string;
  undercardId: string;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

interface CsvRowError {
  rowNumber: number;
  field: string;
  message: string;
}

const COLUMN_TIPS: { col: string; tip: string }[] = [
  { col: 'propType', tip: 'MANUAL_TEAM_THRESHOLD | MANUAL_MATCH_THRESHOLD | MANUAL_PLAYER_BOOLEAN | MANUAL_GENERIC' },
  { col: 'pickMode', tip: 'YES_NO (users pick a side) or BUY_ONLY (legacy buy-and-hope)' },
  { col: 'costCoins', tip: 'Positive integer — what the user pays' },
  { col: 'bonusMultiplier', tip: 'Positive number — multiplied by base points on win' },
  { col: 'subjectTeam', tip: 'For TEAM_THRESHOLD only' },
  { col: 'subjectPlayer', tip: 'For PLAYER_BOOLEAN only' },
  { col: 'threshold', tip: 'Number — required for TEAM/MATCH; optional for PLAYER (blank = any)' },
  { col: 'statCategory', tip: 'GOALS / POINTS / ASSISTS / etc' },
  { col: 'customDescription', tip: 'For GENERIC; optional override on other types' },
  { col: 'gameId', tip: 'Optional — bind to a specific game; mutually exclusive with applyToAllGames' },
  { col: 'applyToAllGames', tip: 'true to fan out across all games (MATCH_THRESHOLD or GENERIC only)' },
];

export function ManualPredictionCsvModal({
  open,
  mainEventId,
  undercardId,
  onClose,
  onSuccess,
}: ManualPredictionCsvModalProps) {
  const [csvText, setCsvText] = useState('');
  const [rowErrors, setRowErrors] = useState<CsvRowError[]>([]);
  const [genericError, setGenericError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showColumnHelp, setShowColumnHelp] = useState(false);

  if (!open) return null;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setCsvText(text);
    setRowErrors([]);
    setGenericError('');
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await authFetch(
        `/api/admin/main-events/${mainEventId}/undercards/${undercardId}/props/manual/bulk`
      );
      if (!res.ok) {
        setGenericError('Failed to download template');
        return;
      }
      const text = await res.text();
      const blob = new Blob([text], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'manual-predictions-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setGenericError('Failed to download template');
    }
  };

  const handleImport = async () => {
    if (submitting) return;
    setRowErrors([]);
    setGenericError('');
    if (!csvText.trim()) {
      setGenericError('Paste or upload CSV content first');
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch(
        `/api/admin/main-events/${mainEventId}/undercards/${undercardId}/props/manual/bulk`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ csv: csvText }),
        }
      );
      const data = await res.json();
      if (res.ok) {
        onSuccess(`Imported ${data.created} predictions from ${data.rowsParsed} CSV rows`);
        setCsvText('');
        onClose();
      } else if (data.code === 'CSV_VALIDATION_FAILED' && Array.isArray(data.rowErrors)) {
        setRowErrors(data.rowErrors);
      } else {
        setGenericError(data.error || 'Import failed');
      }
    } catch {
      setGenericError('Import failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-4 pt-12">
      <div className="w-full max-w-2xl bg-zinc-900 rounded-xl border border-amber-500/30 shadow-2xl">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Upload className="h-4 w-4 text-amber-400" />
            <h3 className="text-base font-bold text-amber-300">Import Manual Predictions (CSV)</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300 text-xl leading-none">
            ×
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300"
            >
              <Download className="h-3.5 w-3.5" />
              Download template.csv
            </button>
            <button
              type="button"
              onClick={() => setShowColumnHelp((v) => !v)}
              className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200"
            >
              <Info className="h-3.5 w-3.5" />
              {showColumnHelp ? 'Hide' : 'Show'} column reference
            </button>
          </div>

          {showColumnHelp && (
            <div className="rounded-lg bg-zinc-950 border border-zinc-700 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-zinc-900/70">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold text-amber-300 w-44">Column</th>
                    <th className="text-left px-3 py-2 font-bold text-zinc-400">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {COLUMN_TIPS.map((c) => (
                    <tr key={c.col} className="border-t border-zinc-800">
                      <td className="px-3 py-1.5 font-mono text-zinc-200">{c.col}</td>
                      <td className="px-3 py-1.5 text-zinc-400">{c.tip}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 py-2 bg-zinc-900/50 text-[10px] text-zinc-500 border-t border-zinc-800">
                All columns must be present in the header row, in any order. Quoted values support commas and escaped quotes (<code>&quot;&quot;</code>).
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs text-zinc-400 mb-1">Upload CSV file</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              className="w-full text-xs text-zinc-300 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-amber-500/10 file:text-amber-300 file:text-xs file:font-bold hover:file:bg-amber-500/20"
            />
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1">…or paste CSV content</label>
            <textarea
              value={csvText}
              onChange={(e) => {
                setCsvText(e.target.value);
                setRowErrors([]);
                setGenericError('');
              }}
              rows={6}
              spellCheck={false}
              placeholder="propType,pickMode,costCoins,bonusMultiplier,..."
              className="w-full px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-700 text-zinc-200 text-xs font-mono focus:border-amber-500 focus:outline-none"
            />
          </div>

          {genericError && (
            <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs">
              {genericError}
            </div>
          )}

          {rowErrors.length > 0 && (
            <div className="rounded-lg bg-red-500/5 border border-red-500/30 overflow-hidden">
              <div className="px-3 py-1.5 bg-red-500/10 text-red-300 text-xs font-bold border-b border-red-500/30">
                {rowErrors.length} validation issue{rowErrors.length === 1 ? '' : 's'} — fix and re-upload
              </div>
              <ul className="text-xs divide-y divide-red-500/10 max-h-48 overflow-y-auto">
                {rowErrors.map((e, i) => (
                  <li key={i} className="px-3 py-1.5 text-zinc-300">
                    <span className="text-red-300 font-mono">
                      {e.rowNumber === 0 ? `[${e.field}]` : `Row ${e.rowNumber}`}
                    </span>{' '}
                    <span className="text-zinc-500">·</span>{' '}
                    <span className="text-amber-300/80">{e.field}</span>
                    <span className="text-zinc-500">:</span> {e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-zinc-800 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-200">
            Cancel
          </button>
          <Button type="button" size="sm" onClick={handleImport} disabled={submitting || !csvText.trim()}>
            {submitting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
            Import
          </Button>
        </div>
      </div>
    </div>
  );
}
